import React, { useEffect } from 'react';
import './Agency.css';
import agencies from './agencies.json';

// Standalone landing page served when an outreach domain is visited directly
// (e.g. alphacloseco.com). Distinct visual identity from Habitat — its own
// brand as a sourcing firm, with Habitat listed only as a partner.
// Reached by hostname, not by route — see resolveAgency().

export function resolveAgency() {
  if (typeof window === 'undefined') return null;
  const ov = new URLSearchParams(window.location.search).get('_agency');
  if (ov && agencies[ov]) return agencies[ov];
  let host = window.location.hostname.toLowerCase().replace(/^www\./, '');
  return agencies[host] || null;
}

// deterministic accent color per agency so they don't all look identical
const ACCENTS = {
  'alphacloseco.com': '#2563eb',   // blue
  'azurestackco.com': '#0891b2',   // cyan
  'azurecloseco.com': '#0d9488',   // teal
  'apexfundio.com':   '#7c3aed',   // violet
};

function initials(name) {
  return name.replace(/[^A-Za-z0-9 ]/g, '').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

function AgencyPage({ agency }) {
  const name = agency.name;
  const isHabitat = agency.kind === 'habitat';
  const host = (typeof window !== 'undefined' ? window.location.hostname.replace(/^www\./, '') : '');
  const accent = ACCENTS[host] || '#2563eb';

  useEffect(() => {
    document.title = name;
    // swap the favicon to a neutral mark (not Habitat's) for agency domains
    let link = document.querySelector("link[rel='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.type = 'image/svg+xml';
    link.href = '/agency-mark.svg';
  }, [name]);

  return (
    <div className="ag-root" style={{ '--accent': accent }}>
      <header className="ag-nav">
        <div className="ag-logo">
          <span className="ag-badge" style={{ background: accent }}>{initials(name)}</span>
          <span className="ag-logoname">{name}</span>
        </div>
      </header>

      <main className="ag-main">
        <section className="ag-hero">
          <h1>We find exceptional online businesses<br/>and connect them with serious buyers.</h1>
          <p className="ag-sub">
            {name} identifies high-quality, high-traffic websites and digital products across the web
            and introduces them to long-term acquirers. We are direct, discreet, and find companies
            uniquely suited for their next stage.
          </p>
        </section>

        <section className="ag-grid">
          <div className="ag-card">
            <div className="ag-num">01</div>
            <h3>Discover</h3>
            <p>We surface promising tools, calculators, and content sites through deep organic-search research.</p>
          </div>
          <div className="ag-card">
            <div className="ag-num">02</div>
            <h3>Connect</h3>
            <p>We reach out to owners on behalf of our acquisition partners — a conversation first, never a hard sell.</p>
          </div>
          <div className="ag-card">
            <div className="ag-num">03</div>
            <h3>Match</h3>
            <p>If there's a fit, we introduce the owner to the right acquirer and step back to let the conversation unfold.</p>
          </div>
        </section>

        <section className="ag-partner">
          <p className="ag-eyebrow">{isHabitat ? 'Part of' : 'In partnership with'}</p>
          <p className="ag-partner-line">
            <a href="https://habitat.inc" target="_blank" rel="noopener noreferrer">Habitat</a>
            {' '}— a holding company that acquires and grows great tech businesses. Backed by General Catalyst.
          </p>
        </section>
      </main>

      <footer className="ag-footer">
        <span>© {name}</span>
      </footer>
    </div>
  );
}

export default AgencyPage;
